import { Ajuda } from '../../design/Ajuda';
import { useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { lerEquipe, salvarHorariosDoGiro } from '../../giro';
import type { ConfigDoGiro, HorarioDoGiro } from '../../giro';

/**
 * HORÁRIO DO GIRO POR BALCONISTA (dono, 28/09): "cada balconista tem o seu momento de fazer o
 * Giro". Antes do horário dele, vende normal no InovaPay e o Giro NÃO começa; no horário, o Giro
 * pendente trava as vendas até terminar. Um horário padrão por pessoa e, se quiser, um horário
 * diferente em algum dia da semana. Salva como versão nova da configuração do Giro, com motivo.
 */

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

type Linha = { accountId: string; nome: string };

export function HorariosDoGiro({
  sessao,
  atual,
  aoSalvar,
}: {
  sessao: Sessao;
  atual: ConfigDoGiro;
  aoSalvar: () => void;
}) {
  const [pessoas, setPessoas] = useState<Linha[] | null>(null);
  const [horarios, setHorarios] = useState<Record<string, HorarioDoGiro>>(atual.horarios);
  const [semHorario, setSemHorario] = useState(atual.sem_horario);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);

  useEffect(() => {
    setHorarios(atual.horarios);
    setSemHorario(atual.sem_horario);
  }, [atual]);
  useEffect(() => {
    void lerEquipe().then((r) => {
      if (!r.ok) {
        setPessoas([]);
        return;
      }
      const vistos = new Map(r.dados.pessoas.map((p) => [p.accountId, p.nome]));
      // quem tem horário salvo mas saiu do Giro continua na lista (dá para apagar)
      for (const id of Object.keys(atual.horarios))
        if (!vistos.has(id)) vistos.set(id, '(fora do Giro)');
      setPessoas(
        [...vistos]
          .map(([accountId, nome]) => ({ accountId, nome }))
          .sort((a, b) => a.nome.localeCompare(b.nome)),
      );
    });
  }, [atual.horarios]);

  const mudou =
    semHorario !== atual.sem_horario ||
    JSON.stringify(limpar(horarios)) !== JSON.stringify(limpar(atual.horarios));
  const padrao = (id: string, v: string) =>
    setHorarios((h) => ({ ...h, [id]: { ...h[id], padrao: v || null } }));
  const doDia = (id: string, dia: number, v: string) =>
    setHorarios((h) => {
      const dias = { ...(h[id]?.dias ?? {}) };
      if (v) dias[String(dia)] = v;
      else delete dias[String(dia)];
      return { ...h, [id]: { padrao: h[id]?.padrao ?? null, dias } };
    });

  const salvar = async () => {
    if (salvando) return;
    setSalvando(true);
    setAviso(null);
    const r = await salvarHorariosDoGiro(sessao, limpar(horarios), semHorario, motivo);
    setSalvando(false);
    if (r.tipo === 'confirmada') {
      setMotivo('');
      setAviso({ tom: 'ok', texto: `Horários salvos (versão ${String(r.dados.versao)}).` });
      aoSalvar();
    } else
      setAviso({
        tom: 'erro',
        texto:
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Não deu para confirmar se salvou. Recarregue a página e confira a versão.',
      });
  };

  return (
    <div className="cv-cartao gc-cartao gc-horarios">
      <h2>
        Horário do Giro de cada balconista
        <Ajuda rotulo="o horário de cada balconista">
          A hora em que o Giro abre para cada pessoa. Antes dela, o balconista vende normal. Se a
          chave &quot;O Giro do dia bloqueia o InovaPay&quot; estiver ligada, a partir dessa hora as
          vendas dele travam até terminar o Giro. &quot;Por dia&quot; muda a hora só em um dia da
          semana.
        </Ajuda>
      </h2>
      <p className="gc-nota">
        Antes do horário, o balconista <b>vende normal</b> no InovaPay e o Giro não começa. No
        horário, o Giro abre e <b>trava as vendas até ele terminar</b>. Deixe em branco para quem
        não tem horário. &quot;Por dia&quot; troca o horário só naquele dia da semana.
      </p>
      {!atual.bloqueia_inovapay && (
        <p className="gc-erro" role="status">
          A chave &quot;O Giro do dia bloqueia o InovaPay&quot; está desligada: os horários valem
          para abrir o Giro, mas nada trava a venda até ela ser ligada.
        </p>
      )}
      <div className="gc-alvo-modo">
        <span className="gc-rotulo">Quem está sem horário</span>
        <div className="cv-segmento" role="tablist" aria-label="Quem está sem horário">
          <button
            type="button"
            role="tab"
            aria-selected={semHorario === 'dia_todo'}
            className={semHorario === 'dia_todo' ? 'cv-sel' : ''}
            onClick={() => setSemHorario('dia_todo')}
          >
            Vale o dia todo
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={semHorario === 'livre'}
            className={semHorario === 'livre' ? 'cv-sel' : ''}
            onClick={() => setSemHorario('livre')}
          >
            Livre, não trava
          </button>
        </div>
        <p className="cv-cinza cv-peq">
          {semHorario === 'dia_todo'
            ? 'Como sempre foi: o Giro abre cedo e trava as vendas até terminar.'
            : 'Faz o Giro quando der; as vendas nunca travam por causa dele.'}
        </p>
      </div>

      {pessoas === null ? (
        <div className="cv-carregando" aria-busy="true" />
      ) : pessoas.length === 0 ? (
        <p className="gc-vazio">Ninguém vinculado a setor do Giro ainda (aba Equipe).</p>
      ) : (
        <ul className="gc-horarios-lista">
          {pessoas.map((p) => {
            const h = horarios[p.accountId];
            const aberto = abertos.has(p.accountId) || Object.keys(h?.dias ?? {}).length > 0;
            return (
              <li key={p.accountId}>
                <div className="gc-horarios-linha">
                  <b>{p.nome}</b>
                  <input
                    type="time"
                    aria-label={`Horário do Giro de ${p.nome}`}
                    value={h?.padrao ?? ''}
                    onChange={(e) => padrao(p.accountId, e.target.value)}
                  />
                  <span className="gc-horarios-sem">
                    {h?.padrao
                      ? ''
                      : semHorario === 'livre'
                        ? 'sem horário · livre'
                        : 'sem horário · dia todo'}
                  </span>
                  <button
                    type="button"
                    className="cv-botao gc-horarios-dia"
                    aria-expanded={aberto}
                    onClick={() =>
                      setAbertos((s) => {
                        const n = new Set(s);
                        if (n.has(p.accountId)) n.delete(p.accountId);
                        else n.add(p.accountId);
                        return n;
                      })
                    }
                  >
                    <Icone nome="clock" tamanho={13} traco={1.7} /> Por dia
                  </button>
                </div>
                {aberto && (
                  <div className="gc-horarios-dias">
                    {DIAS.map((d, i) => (
                      <label key={d} className="gc-campo">
                        <span>{d}</span>
                        <input
                          type="time"
                          aria-label={`${p.nome} — ${d}`}
                          value={h?.dias?.[String(i)] ?? ''}
                          placeholder={h?.padrao ?? ''}
                          onChange={(e) => doDia(p.accountId, i, e.target.value)}
                        />
                      </label>
                    ))}
                    <p className="cv-cinza cv-peq">Dia em branco usa o horário padrão.</p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <label className="gc-campo">
        <span>
          Motivo da mudança <b>obrigatório</b>
        </span>
        <textarea
          rows={2}
          maxLength={500}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: Diego faz o Giro às 10h a partir de hoje"
        />
      </label>
      <div className="gc-acoes">
        <button
          type="button"
          className="cv-botao cv-botao-primario"
          disabled={!mudou || motivo.trim().length < 5 || salvando}
          onClick={() => void salvar()}
        >
          <Icone nome="check" tamanho={14} traco={1.8} />
          {salvando ? 'Salvando…' : 'Salvar horários'}
        </button>
        <button
          type="button"
          className="cv-botao"
          disabled={!mudou || salvando}
          onClick={() => {
            setHorarios(atual.horarios);
            setSemHorario(atual.sem_horario);
          }}
        >
          Descartar
        </button>
        <span className="gc-estado">{mudou ? 'mudanças não salvas' : 'sem mudanças'}</span>
      </div>
      {aviso && (
        <p className={aviso.tom === 'ok' ? 'gc-ok' : 'gc-erro'} role="status">
          {aviso.texto}
        </p>
      )}
    </div>
  );
}

/** Tira quem ficou sem horário nenhum (padrão vazio e nenhum dia) — igual o servidor guarda. */
function limpar(h: Readonly<Record<string, HorarioDoGiro>>): Record<string, HorarioDoGiro> {
  const out: Record<string, HorarioDoGiro> = {};
  for (const id of Object.keys(h).sort()) {
    const x = h[id];
    if (!x) continue;
    const dias = Object.fromEntries(
      Object.entries(x.dias ?? {})
        .filter(([, v]) => !!v)
        .sort(),
    );
    if (!x.padrao && !Object.keys(dias).length) continue;
    out[id] = { padrao: x.padrao || null, ...(Object.keys(dias).length ? { dias } : {}) };
  }
  return out;
}
